import {showToast} from '@/lib/use-sonner';
import {INotification, IStepProgress} from '@/model/Notification';
import {HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {LOCAL_STORAGE_NAMES} from "@/utils/constants.ts";
import {IClientCulture} from "@/model/IClientCulture";
import {t} from "i18next";

const NotificationContext = React.createContext<{
  executing: boolean;
  setExecuting: React.Dispatch<React.SetStateAction<boolean>>;
  setFunctionToReload: React.Dispatch<React.SetStateAction<any>>;
  connection: HubConnection;
  joinClient: (client: string) => Promise<void>;
  notifications: INotification[];
  stepProgress: IStepProgress[];
  clientName: string | null;
  setClientName: React.Dispatch<React.SetStateAction<string | null>>;
  isExecuting: () => boolean;
}>({
  connection: null,
  executing: false,
  setExecuting: null,
  joinClient: null,
  notifications: null,
  stepProgress: null,
  setFunctionToReload: undefined,
  clientName: null,
  setClientName: null,
  isExecuting: () => false,
});

export function NotificationProvider({children}: {children: React.ReactNode}) {
  const [executing, setExecuting] = useState(false);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [stepProgress, setStepProgress] = useState<IStepProgress[]>([]);
  const [connection, setConnection] = useState<HubConnection>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const functionToReloadRef = useRef<any>(undefined);

  useEffect(() => {
    console.log('NotificationContext status:', { executing, notifications, stepProgress, clientName });
  }, [executing, notifications, stepProgress, clientName]);

  const join = async (client: string) => {
    if (connection) await connection.stop();
    if (client) {
      setClientName(client);
      await joinClient(
        client,
        setConnection,
        setNotifications,
        setStepProgress,
        setExecuting,
        functionToReloadRef
      );
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        executing,
        setExecuting,
        connection,
        joinClient: join,
        notifications,
        stepProgress,
        setFunctionToReload: (fn) => functionToReloadRef.current = fn,
        clientName,
        setClientName,
        isExecuting: () => executing,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotificationContext() {
  return useContext(NotificationContext);
}

const setupSignalRListeners = async (
  connection: HubConnection,
  setNotifications: React.Dispatch<React.SetStateAction<INotification[]>>,
  setStepProgress: React.Dispatch<React.SetStateAction<IStepProgress[]>>,
  setExecuting: React.Dispatch<React.SetStateAction<boolean>>,
  functionToReloadRef: React.MutableRefObject<any>
) => {

  connection.onclose((error) => {
    if (error) {
      console.log('(SignalR) Disconnected. error:', error);
    } else {
      console.log('(SignalR) Disconnected.');
    }
  });

  // Connection status (test)
  connection.on('UserConnected', (confirmation: string) => {
    console.log(confirmation);
  });

  // Step Progress
  connection.on('StepProgress', (stepInfo: string) => {
    const stepInfoObj: IStepProgress = JSON.parse(stepInfo);
    setStepProgress((prevSteps) => {
      const stepExists = prevSteps.find((n) => {
        return (
          n.CorrelationId === stepInfoObj.CorrelationId &&
          n.CreatedOn === stepInfoObj.CreatedOn
        );
      });

      if (stepExists) {
        return prevSteps;
      }

      if(stepInfoObj?.TotalSteps > 0 && !stepInfoObj.IsFinished){
        setExecuting(true);
      }

      if(stepInfoObj.IsFinished){
        if(stepInfoObj.RemainingJobsCount == 0){
          if(!stepInfoObj.IsError){
            showToast({
              title: 'Process.Steps.Complete',
              duration: 5000,
              variant: 'success',
            });
          }
          setExecuting(false);
        }

        if (functionToReloadRef.current) {
          try {
            functionToReloadRef.current.refetch();
          } catch (e) {
            console.error(e);
            return [];
          }
        }

        return [];
      }
      return [...prevSteps, stepInfoObj];
    });
  });

  // Info / Success notifications
  connection.on('Notify', (notification: string) => {
    setNotifications((prevNotifications) => {
      const notificationObj: INotification = JSON.parse(notification);
      const notificationExists = prevNotifications.find((n) => {
        return (
          n.CorrelationId === notificationObj.CorrelationId &&
          n.CreatedOn === notificationObj.CreatedOn
        );
      });

      if (notificationExists) {
        return prevNotifications;
      }

      showToast({
        title: notificationObj.Message,
        duration: 5000,
        variant: 'success',
      });

      return [...prevNotifications, notificationObj];
    });
  });

  // Error notifications
  connection.on('LogError', (notification: string) => {
    const notificationObj: INotification = JSON.parse(notification);
    setNotifications((prevNotifications) => {;
      const notificationExists = prevNotifications.find((n) => {
        return (
          n.CorrelationId === notificationObj.CorrelationId &&
          n.CreatedOn === notificationObj.CreatedOn
        );
      });

      if (notificationExists) {
        return prevNotifications;
      }

      showToast({
        title: t(notificationObj.Message) + ' ' + notificationObj.RecordWithError,
        duration: 5000,
        variant: 'destructive',
      });
      return [...prevNotifications, notificationObj];
    });
  });

  await connection.invoke('JoinClient');
};

const joinClient = async (
  client: string,
  setConnection: React.Dispatch<React.SetStateAction<HubConnection>>,
  setNotifications: React.Dispatch<React.SetStateAction<INotification[]>>,
  setStepProgress: React.Dispatch<React.SetStateAction<IStepProgress[]>>,
  setExecuting: React.Dispatch<React.SetStateAction<boolean>>,
  functionToReloadRef: React.MutableRefObject<any>
) =>
{
  try {
    const cultures = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.CULTURES) ?? '[]') as IClientCulture[];
    const cultureId = localStorage.getItem('cultureId')
    const negotiationUrl = import.meta.env.VITE_ICARUS_HUB;

    console.log(`(SignalR) Starting connection... Url: ${negotiationUrl} / Client: ${client}`)

    const connection =
      new HubConnectionBuilder()
      .withUrl(
        negotiationUrl,
        {
          transport: HttpTransportType.LongPolling,
          withCredentials: true,
          headers: {
            'X-Tenant-Id' : client ?? undefined,
            'X-Language-Id' : cultureId ?? undefined,
            'X-Language-Code' : cultures.find(culture => culture.id === +cultureId)?.name ?? undefined,
          }
        },
      )
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    setConnection(connection);

    connection.onreconnected(async () => {
      await setupSignalRListeners(
        connection,
        setNotifications,
        setStepProgress,
        setExecuting,
        functionToReloadRef
      );
    });

    await connection.start().then(async () => {
      await setupSignalRListeners(
        connection,
        setNotifications,
        setStepProgress,
        setExecuting,
        functionToReloadRef
      );
    });
  } catch (e) {
    console.error(e);
  }
}
